import { useState } from 'react';
import { useJobsSummary } from '../../api/jobs';
import { formatPence } from '../../utils/jobsWeek';
import ParentChildDetailView from './ParentChildDetailView';
import type { ChildJobsSummary } from '@family/shared';

const CHILDREN = ['Alex', 'Thomas'] as const;

interface Props {
  isDave: boolean;
}

export default function ParentJobsView({ isDave }: Props) {
  const [selectedChild, setSelectedChild] = useState<string | null>(null);
  const { data, isLoading } = useJobsSummary();

  const summary = data?.data ?? [];
  const summaryByChild = Object.fromEntries(summary.map(s => [s.childName, s]));

  if (selectedChild) {
    return (
      <ParentChildDetailView
        childName={selectedChild}
        isDave={isDave}
        onBack={() => setSelectedChild(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900">Jobs</h2>

      {isLoading ? (
        <div className="flex items-center justify-center h-40 text-gray-500">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {CHILDREN.map(childName => {
            const stats: ChildJobsSummary = summaryByChild[childName] ?? {
              childName,
              currentWeekJobCount: 0,
              currentWeekEarnedPence: 0,
              outstandingPence: 0,
              pendingApprovalCount: 0,
            };

            const hasPending = stats.pendingApprovalCount > 0;
            const hasOutstanding = stats.outstandingPence > 0;

            return (
              <button
                key={childName}
                onClick={() => setSelectedChild(childName)}
                className="text-left bg-white rounded-2xl shadow-sm border border-gray-100 p-5 hover:shadow-md hover:border-gray-200 transition-all space-y-4"
              >
                {/* Child name + alert indicator */}
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 text-base">{childName}</h3>
                  {(hasPending || hasOutstanding) && (
                    <span className="w-2.5 h-2.5 rounded-full bg-orange-400" title="Needs attention" />
                  )}
                </div>

                {/* Stats */}
                <div className="space-y-3">
                  {/* This week */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">This week</span>
                    <div className="text-right">
                      <span className="text-sm font-semibold text-gray-900">
                        {stats.currentWeekJobCount} job{stats.currentWeekJobCount !== 1 ? 's' : ''}
                      </span>
                      <span className="text-sm text-gray-400 mx-1">·</span>
                      <span className="text-sm font-semibold text-green-700">
                        {formatPence(stats.currentWeekEarnedPence)}
                      </span>
                    </div>
                  </div>

                  {/* Outstanding pay */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Outstanding pay</span>
                    <span
                      className={`text-sm font-semibold px-2 py-0.5 rounded-full ${
                        hasOutstanding
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {formatPence(stats.outstandingPence)}
                    </span>
                  </div>

                  {/* Pending approval — only shown when relevant */}
                  {hasPending && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Awaiting approval</span>
                      <span className="text-sm font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                        {stats.pendingApprovalCount}
                      </span>
                    </div>
                  )}
                </div>

                <div className="text-xs text-gray-400 pt-1">Tap to view details →</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
